import { useEffect, useMemo, useState } from 'react';
import { listRows } from '@/api/supabaseClient';
import { normalizePublicRoute, usePublicRoutes } from '@/lib/publicRoutes';
import { useCdiAge } from '@/hooks/useCdiAge';
import { interpolateCdiAge } from '../../shared/institutionalAge.js';

export const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://preview-cdipremium.pages.dev';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/images/cdi-premium-logo.webp`;

const FALLBACK_CONFIG = {
  canonical_domain: SITE_URL,
  default_title: 'CDI Premium — {{idade_cdi}} Anos de Excelência em Diagnósticos por Imagem em Goiânia',
  title_suffix: ' | CDI Premium',
  default_description: 'CDI Premium — clínica de diagnósticos por imagem em Goiânia. Ecocardiografia, Doppler, Medicina Nuclear, Ultrassonografia e mais.',
  default_keywords: '', author: 'CDI Premium', allow_indexing: true,
  default_social_image_url: DEFAULT_OG_IMAGE, og_site_name: 'CDI Premium', og_locale: 'pt_BR',
  twitter_card: 'summary_large_image', twitter_site: '', google_verification: '', bing_verification: '',
};

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(data, id) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

/**
 * Reusable SEO component — dynamically manages document title,
 * meta tags, canonical URL, Open Graph, Twitter Cards, and JSON-LD
 * structured data per page.
 */
export default function Seo({
  title,
  description,
  keywords = '',
  image = '',
  path = '',
  pageKey = '',
  type = 'website',
  jsonLd = null,
  noindex = false,
}) {
  const cdiAge = useCdiAge();
  const routes = usePublicRoutes();
  const resolvedPath = path || (pageKey ? routes.path(pageKey) : '/');
  const configuredPage = pageKey && normalizePublicRoute(resolvedPath) === routes.path(pageKey)
    ? routes.pages[pageKey]
    : null;
  const [config, setConfig] = useState(FALLBACK_CONFIG);

  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(() => {
      listRows('seo_config', { orderBy: null, limit: 1 }).then(configs => {
        if (active) setConfig({ ...FALLBACK_CONFIG, ...(configs[0] || {}) });
      }).catch(() => {
        // The local values below remain active if Supabase is offline.
      });
    }, 2_500);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, []);

  const resolved = useMemo(() => {
    const page = configuredPage;
    const base = (config.canonical_domain || SITE_URL).replace(/\/$/, '');
    const rawTitleTemplate = page?.title || title || config.default_title;
    const rawTitle = interpolateCdiAge(rawTitleTemplate);
    const defaultTitle = interpolateCdiAge(config.default_title);
    const titleSuffix = interpolateCdiAge(config.title_suffix || '');
    const fullTitle = rawTitle === defaultTitle || rawTitle.includes('CDI Premium')
      ? rawTitle
      : `${rawTitle}${titleSuffix}`;
    return {
      config,
      fullUrl: interpolateCdiAge(page?.canonical_url || `${base}${resolvedPath || '/'}`),
      fullTitle,
      desc: interpolateCdiAge(page?.description || description || config.default_description),
      keywords: interpolateCdiAge(page?.keywords || keywords || config.default_keywords || ''),
      img: interpolateCdiAge(page?.social_image_url || image || config.default_social_image_url || DEFAULT_OG_IMAGE),
      noindex: noindex || !config.allow_indexing || page?.index_page === false,
      nofollow: page?.follow_links === false,
    };
  }, [cdiAge, config, configuredPage, description, image, keywords, noindex, resolvedPath, title]);

  const resolvedJsonLd = useMemo(() => interpolateCdiAge(jsonLd), [cdiAge, jsonLd]);

  useEffect(() => {
    const { config, fullTitle, fullUrl, desc, img, noindex: resolvedNoindex, nofollow } = resolved;
    document.title = fullTitle;

    upsertMeta('name', 'description', desc);
    if (resolved.keywords) upsertMeta('name', 'keywords', resolved.keywords);
    upsertMeta('name', 'author', interpolateCdiAge(config.author));
    upsertMeta('name', 'robots', `${resolvedNoindex ? 'noindex' : 'index'}, ${nofollow ? 'nofollow' : 'follow'}`);

    // Open Graph
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', desc);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:url', fullUrl);
    upsertMeta('property', 'og:image', img);
    upsertMeta('property', 'og:site_name', interpolateCdiAge(config.og_site_name));
    upsertMeta('property', 'og:locale', config.og_locale);

    // Twitter Card
    upsertMeta('name', 'twitter:card', config.twitter_card);
    if (config.twitter_site) upsertMeta('name', 'twitter:site', config.twitter_site);
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', desc);
    upsertMeta('name', 'twitter:image', img);

    // Canonical
    upsertCanonical(fullUrl);
    if (config.google_verification) upsertMeta('name', 'google-site-verification', config.google_verification);
    if (config.bing_verification) upsertMeta('name', 'msvalidate.01', config.bing_verification);

    // Page-level JSON-LD (clean previous, set new)
    removeJsonLd('jsonld-page');
    if (resolvedJsonLd) {
      const data = Array.isArray(resolvedJsonLd) ? resolvedJsonLd : [resolvedJsonLd];
      data.forEach((d, i) => upsertJsonLd(d, `jsonld-page-${i}`));
    }

    return () => {
      // Clean page-level JSON-LD on unmount
      const tags = document.head.querySelectorAll('script[id^="jsonld-page-"]');
      tags.forEach(t => t.remove());
    };
  }, [resolved, type, resolvedJsonLd]);

  return null;
}
